/**
 * What one rendered frame actually puts on the canvas, and in which order.
 *
 * Split out of `flight-scene.ts` when AD-015's kill camera made the answer
 * "two passes" instead of one — the host owns the loop, the sim and the scene
 * graph, and it sits close enough to aislop's size ceiling that a cohesive
 * addition belongs in a sibling (the same call `scene-propellers.ts` and
 * `scene-flyby-zoom.ts` record).
 *
 * The order is not arbitrary and is the whole reason this is one function:
 *
 * 1. **Sprites and propeller discs.** Both are built against an already-placed
 *    camera — the sprite quads are CPU-billboarded off it and sized against its
 *    vertical field of view (`three/sprite-screen-size.ts`) — so they are
 *    emitted after `updateCamera` and before anything draws.
 * 2. **The kill camera is *advanced*.** Before the main pass, so the window
 *    opens on the same frame the fatal hit lands rather than one late.
 * 3. **The main view**, full-frame, clearing colour and depth.
 * 4. **The kill camera is *drawn*.** Strictly after, because its pass loads the
 *    colour attachment the main pass just wrote and only clears depth
 *    (`scene-killcam.ts` explains why that is the shape a scissored second view
 *    has to take under WebGPU).
 *
 * Costs nothing while the window is shut: step 4 is one comparison and step 2
 * is a walk of the roster the frame has already fetched.
 */

import type { Pose, SimState } from "@red-opus/engine";
import type { CameraMode } from "./camera-modes.ts";
import { fireSourcesFor, smokeSourcesFor } from "./frame-signals.ts";
import { CAMERA_FOV_DEG, type CameraRig } from "./flight-view.ts";
import { particleFrameFor, type ParticleHost } from "./scene-particles.ts";
import { propellerSourcesFor, spinPlayerModelledPropeller, type PropellerHost } from "./scene-propellers.ts";
import { presentKillCam, updateKillCam, type KillCamHost } from "./scene-killcam.ts";
import { updateSearchlights, type SearchlightHost } from "./scene-searchlights.ts";
import { updateGroundShadows, type ShadowHost } from "./scene-shadows.ts";
import { updateCrashMarks, type CrashMarkHost } from "./scene-crash-marks.ts";
import { updateAmbientJumpers, type AmbientJumperHost } from "./scene-ambient-jumpers.ts";
import type { ParachuteVisual } from "./scene-parachute.ts";

/**
 * The slice of the flight scene a frame's drawing needs — the union of what the
 * propeller discs, the particles and the kill camera each ask for, plus the
 * main camera and which view it is in. Structural, so the host's own
 * `SceneState` stays private to `flight-scene.ts`.
 */
export interface DrawHost
  extends PropellerHost,
    ParticleHost,
    KillCamHost,
    SearchlightHost,
    ShadowHost,
    CrashMarkHost,
    AmbientJumperHost {
  readonly rig: CameraRig;
  readonly cameraMode: CameraMode;
  /** AD-021's canopy. Optional so a host that never bails (a replay surface) need not build one. */
  readonly parachute?: ParachuteVisual;
}

export function drawFrame(
  host: DrawHost,
  dt: number,
  pose: Pose,
  simState: SimState,
  onKillCam: ((open: boolean) => void) | undefined,
): void {
  const fovYRadians = (CAMERA_FOV_DEG * Math.PI) / 180;
  // ENHANCED EFFECTS draws burning and hurt *aeroplanes* — and a working
  // train's coal plume — as particles, so the sprite path is told to leave them
  // out. One options object, read once by both lists, so the two systems cannot
  // both claim the same source (`frame-signals.ts`).
  const sources = { omitAircraft: host.effects.enhancedEffects, omitTrainPlume: host.effects.enhancedEffects };
  const sprites = {
    smokeSources: smokeSourcesFor(host.runner, pose, sources),
    fires: fireSourcesFor(host.runner, pose, sources),
  };
  host.sprites.update(dt, host.rig.camera, fovYRadians, sprites);
  host.particles.update(dt, host.rig.camera, particleFrameFor(host, pose));
  // One list, read twice: AD-012's disc layer draws it, and the player's own
  // modelled blade turns on the rate in it. Two propellers, one rate — and on
  // the four airframes the layer declines, the blade is the only one of them
  // (`propeller-blade-spin.ts`).
  const propellers = propellerSourcesFor(host, pose, simState);
  host.propellers?.update(dt, propellers, host.cameraMode === "cockpit");
  spinPlayerModelledPropeller(host, propellers, dt);
  // World geometry, not billboards, so the camera does not matter — but the
  // mounts slew on `dt` and must be advanced exactly once a frame, which is why
  // this sits here and not in the kill camera's re-draw.
  updateSearchlights(host, dt);
  // World geometry too, and placed from the same interpolated poses the fleet
  // was placed from a few lines up — so an aeroplane and its shadow can never be
  // a frame apart (`scene-shadows.ts`).
  updateGroundShadows(host, pose);
  // World geometry as well, and read straight off the encounter rather than off
  // a pose — a scorch does not move, so this is a placement pass rather than an
  // animation one and it costs nothing on the ordinary frame where nobody has
  // crashed (`scene-crash-marks.ts`).
  updateCrashMarks(host);
  // AD-021's canopy. World geometry, read straight off the encounter like the
  // crash marks above — so the kill camera draws it correctly with no
  // re-emission, which a billboard would have needed
  // (`scene-parachute.ts`).
  host.parachute?.update(host.runner.encounter().parachute, dt);
  // AD-021b's ambience: enemy pilots leaving truly doomed aeroplanes. The same
  // silk as the line above, from the same kit, and the same read-only treatment
  // of the encounter the crash marks get — it takes no `dt`, because every
  // canopy's age is measured against the encounter's own clock so a replay puts
  // them in the same fields (`scene-ambient-jumpers.ts`).
  updateAmbientJumpers(host);
  const victim = updateKillCam(host, dt);
  host.renderer.render(host.scene, host.rig.camera);
  presentKillCam(host, victim, fovYRadians, sprites, onKillCam);
}
